import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  PasswordInputOnly,
  PasswordValidationAndSubmit,
} from '@features/onboarding/components/shared/PasswordInputWithValidation';
import { AuthLayout } from '@features/auth/layouts/AuthLayout';
import { useResetPassword } from '@features/auth/lib/useResetPassword';

import { FormField, FormItem, FormControl } from '@components/ui/form';
import { Form } from '@components/ui/form';
import { FloatingInput } from '@components/ui/input-floating';
import { DevFillButton, DEVFILL_TID } from 'src/devtools/DevFillButton';
import { DEVFILL_ENABLED } from 'src/devtools/gate';
import { buildResetPassword, fillWith } from 'src/devtools/fillers/auth';

const ResetPasswordSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters long.')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter.')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter.')
      .regex(/[0-9]/, 'Password must contain at least one number.')
      .regex(/[^A-Za-z0-9\s]/, 'Password must contain at least one special character.'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match.",
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof ResetPasswordSchema>;

interface ResetPasswordFormProps {
  /** Post-success redirect AND the "log in" link target (default B2C `/login`). */
  loginPath?: string;
  /** When set, a failed reset offers a re-request link to this path (frozen
   *  C-6: "inline error … with a link to sibling forgot"). Unset in B2C — no
   *  affordance renders, B2C unchanged. */
  forgotPath?: string;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  loginPath = '/login',
  forgotPath,
}) => {
  const { resetPassword, error, isLoading, success, clear } = useResetPassword();
  const [ disabledSubmit, setDisabledSubmit ] = useState(false);
  const navigate = useNavigate();

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    // mode: 'onChange',
  });

  const password = form.watch('password');

  const {
    formState: { errors },
    clearErrors,
  } = form;

  useEffect(() => {
    setDisabledSubmit(!!Object.values(errors).length);
  }, [JSON.stringify(errors)]);

  useEffect(() => {
    if (!success) return;

    const timeoutId = window.setTimeout(() => {
      navigate(loginPath, { replace: true });
    }, 2000);

    return () => window.clearTimeout(timeoutId);
  }, [success, navigate, loginPath]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    await resetPassword(data);
  };

  return (
    <AuthLayout
      title="Create a new password."
      variant="new-password"
      titleClassName="font-extrabold text-[40px] leading-tight max-md:text-[28px]"
      headerClassName="2xl:pt-20 2xl:px-6 max-md:pb-72"
      contentClassName="2xl:pt-48 2xl:pl-24"
    >
      <div className="w-full max-w-[450px]">
        {DEVFILL_ENABLED && (
          <DevFillButton
            testid={DEVFILL_TID.resetPassword}
            label="Fill password"
            onFill={() => fillWith(buildResetPassword, (v) => form.reset(v))}
          />
        )}
        <section className="mb-3">
          <h2 className="text-4xl font-extrabold leading-8 mb-2">
            Enter a new password.
          </h2>
          <p className="text-[14px] max-sm:max-w-87 flex-wrap max-w-[430px]">
            Enter a new password and then enter it again to confirm that it's
            correct.
          </p>
        </section>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="max-w-[430px]"
          >
            <PasswordInputOnly form={form} label="Enter a new password" />

            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormControl>
                    <FloatingInput
                      id="confirmPassword"
                      label="Confirm your password"
                      type="password"
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        clearErrors('confirmPassword');
                        clear();
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      inputClass="h-16 rounded-lg mt-5 border border-[#B7B7B7] p-2 w-full max-w-107.5 max-sm:max-w-full disabled:text-[#ffffff55] disabled:opacity-100 "
                    />
                  </FormControl>
                  {fieldState.error && (
                    <p className="text-error text-sm mt-1">
                      {fieldState.error.message}
                    </p>
                  )}
                </FormItem>
              )}
            />
            <PasswordValidationAndSubmit
              form={form}
              onSubmit={form.handleSubmit(onSubmit)}
              buttonText={isLoading ? 'Submitting...' : 'SUBMIT'}
              isSubmitDisabled={disabledSubmit || isLoading}
            >
              {error && <p className="text-error text-sm mt-4 -mb-4">{error}</p>}
              {error && forgotPath && (
                <p className="text-sm mt-6 -mb-6">
                  <Link
                    to={forgotPath}
                    className="text-primary font-extrabold hover:underline"
                  >
                    Request a new reset link
                  </Link>
                </p>
              )}
              {success && <p className="text-green-700 text-sm font-medium mt-4 -mb-4">{success}</p>}
            </PasswordValidationAndSubmit>

            <p className="text-base xs:text-xl font-extrabold mt-10 text-center lg:text-left w-full max-w-[430px]">
              Returning user?{' '}
              <Link
                to={loginPath}
                className="text-primary hover:underline whitespace-nowrap"
              >
                Click here to log in.
              </Link>
            </p>
          </form>
        </Form>
      </div>
    </AuthLayout>
  );
};
